import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Forward10, Pause, PlayArrow, Replay10, VolumeOff, VolumeUp } from '@mui/icons-material';
import styled from 'styled-components';
import TranscriptWithAudio from 'components/UI/transcriptWithAudio/transcriptWithAudio';
import Loading from 'components/Loading';
import COLOR from 'utils/constants/color';
import {
    isIOSAudioDevice,
    normalizeAudioPlaybackUrl,
    prepareAudioBlobForPlayback,
    urlNeedsBlobPlayback,
} from 'components/NativeAudioPlayer';

const isIPadDevice = () => {
    if (typeof navigator === 'undefined') return false;
    if (/iPad/i.test(navigator.userAgent)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const getFiniteDuration = (el) => {
    if (!el) return 0;
    const value = el.duration;
    if (Number.isFinite(value) && value > 0) return value;
    return 0;
};

const probeAudioDuration = (el) => new Promise((resolve) => {
    if (!el) {
        resolve(0);
        return;
    }
    const existing = getFiniteDuration(el);
    if (existing > 0) {
        resolve(existing);
        return;
    }
    let settled = false;
    const finish = (value) => {
        if (settled) return;
        settled = true;
        cleanup();
        const duration = getFiniteDuration(el) || value || 0;
        if (duration > 0) {
            const resumeAt = Math.min(el.currentTime || 0, duration);
            el.currentTime = resumeAt;
        }
        resolve(duration);
    };
    const cleanup = () => {
        el.removeEventListener('durationchange', onUpdate);
        el.removeEventListener('loadedmetadata', onUpdate);
        el.removeEventListener('timeupdate', onUpdate);
    };
    const onUpdate = () => {
        const duration = getFiniteDuration(el);
        if (duration > 0) finish(duration);
    };
    el.addEventListener('durationchange', onUpdate);
    el.addEventListener('loadedmetadata', onUpdate);
    el.addEventListener('timeupdate', onUpdate);
    try {
        el.currentTime = Number.MAX_SAFE_INTEGER;
    } catch {
        finish(0);
    }
    window.setTimeout(() => finish(0), 4000);
});

const PlayerWrap = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
`;

const ControlsRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const IconButton = styled.button`
    width: 36px;
    height: 36px;
    min-width: 36px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
    background: ${p => (p.$primary ? COLOR.green100 : COLOR.white)};
    color: ${p => (p.$primary ? COLOR.white : COLOR.green100)};
    border: 1px solid ${COLOR.green100};
    opacity: ${p => (p.$disabled ? 0.5 : 1)};
    padding: 0;

    &:hover {
        opacity: ${p => (p.$disabled ? 0.5 : 0.9)};
    }
`;

const SeekWrap = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
`;

const SeekInput = styled.input`
    width: 100%;
    height: 4px;
    appearance: none;
    background: ${COLOR.black700};
    border-radius: 4px;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
        appearance: none;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: ${COLOR.green100};
        cursor: pointer;
    }

    &::-moz-range-thumb {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: ${COLOR.green100};
        border: none;
        cursor: pointer;
    }
`;

const TimeText = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${COLOR.black300};
    white-space: nowrap;
    min-width: 72px;
    text-align: right;
`;

const RetryButton = styled.button`
    font-size: 12px;
    border: 1px solid ${COLOR.green100};
    background: ${COLOR.white};
    color: ${COLOR.green100};
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    align-self: flex-start;
`;

const VolumeControlWrap = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 4px;
`;

const VolumeInput = styled(SeekInput)`
    width: 72px;
    flex: none;
`;

const CustomAudioPlayer = ({
    url,
    onRef,
    onPlay,
    onCanPlayThrough,
    style = {},
    autoPlay = false,
}) => {
    const audioRef = useRef(null);
    const blobUrlRef = useRef(null);
    const fetchAttemptedRef = useRef(false);
    const mimeRetryIndexRef = useRef(0);
    const durationProbedRef = useRef(false);
    const volumeWrapRef = useRef(null);
    const lastVolumeBeforeMuteRef = useRef(1);
    const [playbackSrc, setPlaybackSrc] = useState('');
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const isIPad = useMemo(() => isIPadDevice(), []);
    const normalizedUrl = useMemo(() => normalizeAudioPlaybackUrl(url), [url]);
    const shouldUseBlobPlayback = useMemo(() => urlNeedsBlobPlayback(normalizedUrl), [normalizedUrl]);

    const setAudioRef = useCallback((el) => {
        audioRef.current = el;
        if (onRef) onRef(el);
    }, [onRef]);

    const applyPlaybackBlob = useCallback((objectUrl) => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = objectUrl;
        setPlaybackSrc(objectUrl);
        setHasError(false);
    }, []);

    const tryBlobFallback = useCallback(async (forcedMime = '') => {
        if (!normalizedUrl) return;
        if (fetchAttemptedRef.current && !forcedMime) return;
        if (!forcedMime) fetchAttemptedRef.current = true;
        setIsLoading(true);
        try {
            const response = await fetch(normalizedUrl);
            if (!response.ok) {
                setHasError(true);
                setPlaybackSrc(normalizedUrl);
                setIsLoading(false);
                return;
            }
            const rawBlob = await response.blob();
            const typedBlob = forcedMime
                ? new Blob([await rawBlob.arrayBuffer()], { type: forcedMime })
                : await prepareAudioBlobForPlayback(
                    rawBlob,
                    normalizedUrl,
                    response.headers.get('content-type') || '',
                );
            applyPlaybackBlob(URL.createObjectURL(typedBlob));
        } catch {
            setHasError(true);
            setPlaybackSrc(normalizedUrl);
            setIsLoading(false);
        }
    }, [normalizedUrl, applyPlaybackBlob]);

    const updateDuration = useCallback(async () => {
        const el = audioRef.current;
        if (!el) return;
        const known = getFiniteDuration(el);
        if (known > 0) {
            setDuration(known);
            return;
        }
        if (durationProbedRef.current) return;
        durationProbedRef.current = true;
        const probed = await probeAudioDuration(el);
        if (probed > 0) setDuration(probed);
    }, []);

    const syncTime = useCallback(() => {
        const el = audioRef.current;
        if (!el) return;
        setCurrentTime(el.currentTime || 0);
        const known = getFiniteDuration(el);
        if (known > 0) setDuration(known);
    }, []);

    useEffect(() => {
        fetchAttemptedRef.current = false;
        mimeRetryIndexRef.current = 0;
        durationProbedRef.current = false;
        setHasError(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setIsLoading(true);
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        if (!normalizedUrl) {
            setPlaybackSrc('');
            return;
        }
        if (shouldUseBlobPlayback) {
            tryBlobFallback();
        } else {
            setPlaybackSrc(normalizedUrl);
        }
    }, [normalizedUrl, shouldUseBlobPlayback, tryBlobFallback]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el || !playbackSrc) return;
        el.load();
        const handleReady = () => {
            updateDuration();
        };
        el.addEventListener('loadedmetadata', handleReady);
        el.addEventListener('durationchange', handleReady);
        return () => {
            el.removeEventListener('loadedmetadata', handleReady);
            el.removeEventListener('durationchange', handleReady);
        };
    }, [playbackSrc, updateDuration]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el || !autoPlay) return;
        const handleCanPlay = () => {
            el.play().catch(() => {});
            if (onCanPlayThrough) onCanPlayThrough(el);
        };
        el.addEventListener('canplaythrough', handleCanPlay, { once: true });
        return () => el.removeEventListener('canplaythrough', handleCanPlay);
    }, [autoPlay, playbackSrc, onCanPlayThrough]);

    useEffect(() => () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        if (isIPad) {
            el.muted = isMuted;
            if (!isMuted) el.volume = volume;
            return;
        }
        el.muted = isMuted || volume === 0;
        el.volume = volume;
    }, [volume, isMuted, isIPad, playbackSrc]);

    useEffect(() => {
        if (!showVolumeSlider || isIPad) return undefined;
        const handlePointerOutside = (event) => {
            if (volumeWrapRef.current && !volumeWrapRef.current.contains(event.target)) {
                setShowVolumeSlider(false);
            }
        };
        document.addEventListener('mousedown', handlePointerOutside);
        document.addEventListener('touchstart', handlePointerOutside);
        return () => {
            document.removeEventListener('mousedown', handlePointerOutside);
            document.removeEventListener('touchstart', handlePointerOutside);
        };
    }, [showVolumeSlider, isIPad]);

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el || hasError) return;
        if (el.paused) {
            el.play()
                .then(() => onPlay?.(el))
                .catch(() => {});
        } else {
            el.pause();
        }
    };

    const seekBy = (delta) => {
        const el = audioRef.current;
        if (!el) return;
        const max = Number.isFinite(el.duration) ? el.duration : duration;
        const next = Math.min(Math.max(0, (el.currentTime || 0) + delta), max || 0);
        el.currentTime = next;
        setCurrentTime(next);
    };

    const handleSeek = (e) => {
        const el = audioRef.current;
        if (!el) return;
        const next = Number(e.target.value);
        el.currentTime = next;
        setCurrentTime(next);
    };

    const handleVolumeIconClick = () => {
        if (!playbackSrc || hasError) return;
        if (isIPad) {
            if (isMuted || volume === 0) {
                const restored = lastVolumeBeforeMuteRef.current > 0
                    ? lastVolumeBeforeMuteRef.current
                    : 1;
                setVolume(restored);
                setIsMuted(false);
                const el = audioRef.current;
                if (el) {
                    el.volume = restored;
                    el.muted = false;
                }
                return;
            }
            const el = audioRef.current;
            const current = el?.volume ?? volume;
            if (current > 0) lastVolumeBeforeMuteRef.current = current;
            setIsMuted(true);
            if (el) el.muted = true;
            return;
        }
        setShowVolumeSlider((prev) => !prev);
    };

    const handleVolumeChange = (e) => {
        const next = Number(e.target.value) / 100;
        setVolume(next);
        setIsMuted(next === 0);
        const el = audioRef.current;
        if (el) {
            el.volume = next;
            el.muted = next === 0;
        }
    };

    const handleError = () => {
        if (!fetchAttemptedRef.current) {
            tryBlobFallback();
            return;
        }
        const retryMimes = isIOSAudioDevice()
            ? ['audio/mp4', 'audio/mpeg', 'audio/webm']
            : ['audio/webm', 'audio/mp4', 'audio/mpeg'];
        const nextMime = retryMimes[mimeRetryIndexRef.current];
        if (nextMime) {
            mimeRetryIndexRef.current += 1;
            tryBlobFallback(nextMime);
            return;
        }
        setHasError(true);
        setIsLoading(false);
    };

    if (!normalizedUrl) return null;

    const controlsDisabled = !playbackSrc || hasError;
    const maxDuration = duration > 0 ? duration : 0;
    const volumeIconMuted = isMuted || volume === 0;
    const volumeSliderValue = Math.round(volume * 100);

    return (
        <PlayerWrap style={style}>
            <audio
                ref={setAudioRef}
                playsInline
                preload="auto"
                src={playbackSrc || undefined}
                style={{ display: 'none' }}
                onTimeUpdate={syncTime}
                onLoadedMetadata={() => {
                    syncTime();
                    updateDuration();
                    setIsLoading(false);
                }}
                onDurationChange={() => {
                    syncTime();
                    updateDuration();
                }}
                onLoadedData={() => {
                    syncTime();
                    updateDuration();
                }}
                onPlay={() => {
                    setIsPlaying(true);
                    setIsLoading(false);
                    onPlay?.(audioRef.current);
                }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onWaiting={() => setIsLoading(true)}
                onCanPlay={() => setIsLoading(false)}
                onCanPlayThrough={() => {
                    setIsLoading(false);
                    onCanPlayThrough?.(audioRef.current);
                }}
                onError={handleError}
            />
            <ControlsRow>
                <IconButton
                    type="button"
                    $disabled={controlsDisabled}
                    disabled={controlsDisabled}
                    onClick={() => seekBy(-10)}
                    aria-label="Rewind 10 seconds"
                >
                    <Replay10 fontSize="small" />
                </IconButton>
                <IconButton
                    type="button"
                    $primary
                    $disabled={controlsDisabled}
                    disabled={controlsDisabled}
                    onClick={togglePlay}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isLoading && !hasError ? (
                        <Loading size={18} white />
                    ) : isPlaying ? (
                        <Pause fontSize="small" />
                    ) : (
                        <PlayArrow fontSize="small" />
                    )}
                </IconButton>
                <IconButton
                    type="button"
                    $disabled={controlsDisabled}
                    disabled={controlsDisabled}
                    onClick={() => seekBy(10)}
                    aria-label="Forward 10 seconds"
                >
                    <Forward10 fontSize="small" />
                </IconButton>
                <SeekWrap>
                    <SeekInput
                        type="range"
                        min={0}
                        max={maxDuration || 0}
                        step={0.1}
                        value={Math.min(currentTime, maxDuration || currentTime)}
                        disabled={controlsDisabled || maxDuration <= 0}
                        onChange={handleSeek}
                    />
                </SeekWrap>
                <TimeText>
                    {formatTime(currentTime)} / {maxDuration > 0 ? formatTime(maxDuration) : '--:--'}
                </TimeText>
                <VolumeControlWrap ref={volumeWrapRef}>
                    {showVolumeSlider && !isIPad ? (
                        <VolumeInput
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={volumeSliderValue}
                            disabled={controlsDisabled}
                            onChange={handleVolumeChange}
                            aria-label="Volume"
                        />
                    ) : null}
                    <IconButton
                        type="button"
                        $disabled={controlsDisabled}
                        disabled={controlsDisabled}
                        onClick={handleVolumeIconClick}
                        aria-label={volumeIconMuted ? 'Unmute' : 'Volume'}
                    >
                        {volumeIconMuted ? (
                            <VolumeOff fontSize="small" />
                        ) : (
                            <VolumeUp fontSize="small" />
                        )}
                    </IconButton>
                </VolumeControlWrap>
            </ControlsRow>
            {hasError ? (
                <RetryButton
                    type="button"
                    onClick={() => {
                        fetchAttemptedRef.current = false;
                        mimeRetryIndexRef.current = 0;
                        tryBlobFallback();
                    }}
                >
                    Retry audio
                </RetryButton>
            ) : null}
        </PlayerWrap>
    );
};

export const ShareAssignmentAudioPlayer = ({
    url,
    audioKey,
    audioPlayersRef,
    pauseAllOtherAudioPlayers,
    title = '',
    style = {},
    withTranscript = true,
    autoPlay = false,
    onCanPlayThrough,
}) => {
    const normalizedUrl = normalizeAudioPlaybackUrl(url);
    if (!normalizedUrl) return null;

    if (!audioPlayersRef.current.has(audioKey)) {
        audioPlayersRef.current.set(audioKey, { current: null, hasAutoPlayed: false });
    }
    const playerEntry = audioPlayersRef.current.get(audioKey);

    const handleRef = (el) => {
        playerEntry.current = el;
    };

    const handlePlay = () => {
        if (typeof pauseAllOtherAudioPlayers === 'function') {
            pauseAllOtherAudioPlayers(playerEntry);
        }
    };

    const handleCanPlayThrough = (el) => {
        if (autoPlay && !playerEntry.hasAutoPlayed && el) {
            el.play().catch(() => {});
            playerEntry.hasAutoPlayed = true;
        }
        if (onCanPlayThrough) onCanPlayThrough(el);
    };

    const player = (
        <CustomAudioPlayer
            url={normalizedUrl}
            onRef={handleRef}
            onPlay={handlePlay}
            onCanPlayThrough={handleCanPlayThrough}
            style={style}
            autoPlay={autoPlay}
        />
    );

    if (!withTranscript) return player;

    return (
        <TranscriptWithAudio audioUrl={normalizedUrl} title={title}>
            {player}
        </TranscriptWithAudio>
    );
};

export default CustomAudioPlayer;
